import { useState } from 'react';
import {
  useAddJobMutation,
  useUpdateJobMutation,
} from '../../features/apis/intergration';
import { toast } from 'react-toastify';
import useCurrentUser from '../../customHook/useCurrentUser';
import DocumentsTab from './_AddRoleJob/DocumentsTab';
import RequirementTab from './_AddRoleJob/RequirementTab';
import QuestionsTab from './_AddRoleJob/QuestionsTab';
import { IoMdClose } from 'react-icons/io';
import { useLazyGetJobsByCompanyQuery } from '../../features/apis/intergration';

const TABS = ['Requirement', 'Documents', 'Questions'];

type AddRoleJobProps = {
  open: boolean;
  onClose: () => void;
  job?: any;
};

const validateRequirements = (
  data: any
): { isValid: boolean; errors: string[] } => {
  const requiredFields = [
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'industry', label: 'Industry' },
    { key: 'jobType', label: 'Job Type' },
    { key: 'workMode', label: 'Work Mode' },
    { key: 'location', label: 'Job Location' },
    { key: 'experienceLevel', label: 'Experience Level' },
    { key: 'salaryRange', label: 'Salary Range' },
    { key: 'email', label: 'Recruiter Email' },
    { key: 'jobDesc', label: 'Job Description' },
  ];

  const errors: string[] = [];

  requiredFields.forEach((field) => {
    if (!data[field.key] || data[field.key].toString().trim() === '') {
      errors.push(`${field.label} is required`);
    }
  });

  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Please enter a valid email address');
  }

  return { isValid: errors.length === 0, errors };
};

export default function AddJob({ open, onClose, job }: AddRoleJobProps) {
  const isEditing = !!job;
  const [activeTab, setActiveTab] = useState(0);
  const [addJob, { isLoading: isAdding }] = useAddJobMutation();
  const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();
  const isLoading = isAdding || isUpdating;
  const user = useCurrentUser();
  const [requirementsData, setRequirementsData] = useState<any>(job ?? {});
  const [documentsData, setDocumentsData] = useState<string[]>(
    job?.documents ?? []
  );
  const [questionsData, setQuestionsData] = useState<string[]>(
    job?.questions ?? []
  );
  const [newDoc, setNewDoc] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [getJobsByCompany] = useLazyGetJobsByCompanyQuery();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  if (!open) return null;

  const flushPendingInputs = () => {
    if (newDoc.trim() && !documentsData.includes(newDoc.trim())) {
      setDocumentsData((prev) => [...prev, newDoc.trim()]);
      setNewDoc('');
    }
    if (newQuestion.trim() && !questionsData.includes(newQuestion.trim())) {
      setQuestionsData((prev) => [...prev, newQuestion.trim()]);
      setNewQuestion('');
    }
  };

  const goNext = () => {
    flushPendingInputs();
    if (activeTab === 0) {
      const validation = validateRequirements(requirementsData);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        toast.error('Please fill all required fields in the Requirement tab');
        return;
      } else {
        setValidationErrors([]);
      }
    }
    setActiveTab((t) => Math.min(t + 1, TABS.length - 1));
  };

  const goPrev = () => setActiveTab((t) => Math.max(t - 1, 0));

  const handleSubmit = async () => {
    const finalQuestions =
      newQuestion.trim() && !questionsData.includes(newQuestion.trim())
        ? [...questionsData, newQuestion.trim()]
        : questionsData;

    const finalDocs =
      newDoc.trim() && !documentsData.includes(newDoc.trim())
        ? [...documentsData, newDoc.trim()]
        : documentsData;

    try {
      const payload = {
        companyId: user?.companyId,
        ...requirementsData,
        questions: finalQuestions,
        documents: finalDocs,
      };

      if (isEditing) {
        await updateJob({ jobId: job._id, data: payload }).unwrap();
        toast.success('Job updated successfully!');
      } else {
        await addJob(payload).unwrap();
        toast.success('Job added successfully!');
      }

      getJobsByCompany(user?.companyId);
      onClose();
      setRequirementsData({});
      setDocumentsData([]);
      setQuestionsData([]);
      setNewDoc('');
      setNewQuestion('');
      setActiveTab(0);
    } catch (error: any) {
      toast.error(
        error?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} job`
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full sm:max-w-3xl bg-white sm:rounded-2xl mx-2 rounded-t-2xl shadow-2xl overflow-hidden max-h-[95dvh] sm:max-h-[90vh] flex flex-col">
        <div className="px-4 sm:px-8 pt-4 sm:pt-6 pb-0 flex items-center justify-between shrink-0">
          <h1 className="text-lg sm:text-xl text-gray-900 font-bold">
            {isEditing ? 'Edit Job' : 'Add Job'}
          </h1>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-red-500 transition"
          >
            <IoMdClose size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 sm:px-8 pt-3 sm:pt-4 flex border-b border-[#EEEEEE] gap-2 sm:gap-6 shrink-0 overflow-x-auto">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => {
                if (activeTab === 0 && i > 0) {
                  const validation = validateRequirements(requirementsData);
                  if (!validation.isValid) {
                    setValidationErrors(validation.errors);
                    toast.error(
                      'Please fill all required fields in the Requirement tab'
                    );
                    return;
                  } else {
                    setValidationErrors([]);
                  }
                }
                flushPendingInputs();
                setActiveTab(i);
              }}
              disabled={
                activeTab === 0 &&
                i > 0 &&
                !validateRequirements(requirementsData).isValid
              }
              className={`py-2 sm:py-2.5 px-3 sm:px-5 text-xs sm:text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeTab === i
                  ? 'border-[#6E3EF5] text-gray-900 font-semibold'
                  : activeTab === 0 &&
                    i > 0 &&
                    !validateRequirements(requirementsData).isValid
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : 'border-transparent text-[#AAAAAA] hover:text-gray-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div className="px-4 sm:px-8 py-4 sm:py-7 overflow-y-auto flex-1">
          {validationErrors.length > 0 && activeTab === 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-red-800 text-xs sm:text-sm font-medium mb-1">
                Please fix the following errors:
              </div>
              <ul className="text-red-700 text-xs space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 0 && (
            <RequirementTab
              data={requirementsData}
              setData={setRequirementsData}
            />
          )}
          {activeTab === 1 && (
            <DocumentsTab
              data={documentsData}
              setData={setDocumentsData}
              newDoc={newDoc}
              setNewDoc={setNewDoc}
            />
          )}
          {activeTab === 2 && (
            <QuestionsTab
              data={questionsData}
              setData={setQuestionsData}
              newQuestion={newQuestion}
              setNewQuestion={setNewQuestion}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center border-t border-[#EEEEEE] shrink-0">
          <button
            onClick={goPrev}
            disabled={activeTab === 0}
            className={`px-4 sm:px-7 py-2 sm:py-2.5 rounded-full border text-xs sm:text-sm font-semibold ${
              activeTab === 0
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-200 text-gray-700 hover:border-gray-400'
            }`}
          >
            ← Back
          </button>

          <div className="flex gap-1.5 items-center">
            {TABS.map((_, i) => (
              <div
                key={i}
                onClick={() => setActiveTab(i)}
                className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
                  i === activeTab ? 'w-5 bg-violet-600' : 'w-2 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <button
            onClick={activeTab === TABS.length - 1 ? handleSubmit : goNext}
            disabled={
              isLoading ||
              (activeTab === 0 &&
                !validateRequirements(requirementsData).isValid)
            }
            className={`px-4 sm:px-7 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
              activeTab === TABS.length - 1
                ? isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#6535EB] to-[#9F3198] text-white hover:opacity-90'
                : validateRequirements(requirementsData).isValid
                ? 'bg-[#6535EB] text-white hover:opacity-90'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {activeTab === TABS.length - 1
              ? isLoading
                ? 'Submitting...'
                : isEditing
                ? 'Save Changes'
                : 'Submit'
              : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}
